'use client'

import { useMemo, useState } from "react"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useFetchLeagues, useFetchLeagueStandings } from "@/lib/api/league-routes"
import type { League, TeamRank } from "@/lib/types/leagues"
import { cn } from "@/lib/utils"

const YEAR_OPTIONS = Array.from({ length: 2026 - 1992 + 1 }, (_, i) => 2026 - i)
const DEFAULT_SEASON = 2025
const DEFAULT_LEAGUE_ID = 2
const TOP_LEAGUE_IDS = [1, 2, 3, 4, 5]
const PINNED_LEAGUE_IDS = [...TOP_LEAGUE_IDS, 7, 8, 9, 10, 11, 12, 13, 202, 24]
// Sentinel league_id (no real league uses 0) selecting the combined "Top 5
// Leagues" view — prints each of TOP_LEAGUE_IDS' own table one after
// another, since standings aren't something that merges across leagues.
const TOP5_SENTINEL = 0

type ViewMode = "table" | "gap"

function LeagueFlagPicker({
  value,
  onChange,
}: {
  value: number
  onChange: (v: number) => void
}) {
  const { data, isLoading } = useFetchLeagues()
  const leagues = useMemo<League[]>(() => {
    const all = data?.data ?? []
    return PINNED_LEAGUE_IDS
      .map((id) => all.find((l) => l.league_id === id))
      .filter((l): l is League => l !== undefined)
  }, [data])

  if (isLoading) return <Skeleton className="h-9 w-full" />

  const selected = leagues.find((l) => l.league_id === value)
  const topLeagues = leagues.filter((l) => TOP_LEAGUE_IDS.includes(l.league_id))
  const otherLeagues = leagues.filter((l) => !TOP_LEAGUE_IDS.includes(l.league_id))

  const renderLeagueItem = (league: League) => (
    <SelectItem key={league.league_id} value={String(league.league_id)}>
      <span className="flex items-center gap-2">
        {league.logo_url ? (
          <img src={league.logo_url} alt="" className="size-4 shrink-0 object-contain" />
        ) : league.country?.flag_url ? (
          <img src={league.country.flag_url} alt="" className="h-3 w-4 shrink-0 rounded-[1px] object-cover" />
        ) : null}
        {league.recent_competition_name ?? league.league_name}
      </span>
    </SelectItem>
  )

  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="w-full">
        <SelectValue>
          {value === TOP5_SENTINEL ? (
            <span className="truncate">Top 5 Leagues</span>
          ) : (
            selected && (
              <span className="flex items-center gap-2 truncate">
                {selected.logo_url ? (
                  <img src={selected.logo_url} alt="" className="size-4 shrink-0 object-contain" />
                ) : selected.country?.flag_url ? (
                  <img src={selected.country.flag_url} alt="" className="h-3 w-4 shrink-0 rounded-[1px] object-cover" />
                ) : null}
                <span className="truncate">{selected.recent_competition_name ?? selected.league_name}</span>
              </span>
            )
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-[260px] overflow-y-auto">
        <SelectItem value={String(TOP5_SENTINEL)}>Top 5 Leagues</SelectItem>
        <SelectGroup>
          <SelectLabel>Top Leagues</SelectLabel>
          {topLeagues.map(renderLeagueItem)}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Other Leagues</SelectLabel>
          {otherLeagues.map(renderLeagueItem)}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

function TeamLogoImg({
  team,
  className,
}: {
  team: TeamRank["team"]
  className?: string
}) {
  const label = team.common_name ?? team.team_name ?? ""
  return team.logo_url ? (
    <img
      src={team.logo_url}
      alt=""
      title={label}
      className={cn("object-contain shrink-0", className ?? "size-5")}
    />
  ) : (
    <span
      title={label}
      className={cn(
        "flex items-center justify-center font-bold text-[9px] text-muted-foreground bg-muted rounded shrink-0",
        className ?? "size-5"
      )}
    >
      {label.charAt(0).toUpperCase()}
    </span>
  )
}

function GdLabel({ gd }: { gd: number | null | undefined }) {
  if (gd == null) return <span className="text-muted-foreground/40">—</span>
  if (gd > 0) return <span className="text-green-500">+{gd}</span>
  if (gd < 0) return <span className="text-red-400">{gd}</span>
  return <span className="text-muted-foreground/40">0</span>
}

function TableView({ standings }: { standings: TeamRank[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 pb-1.5 border-b border-border/60 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/35 select-none">
        <span className="w-5 shrink-0 text-right">#</span>
        <span className="flex-1 pl-7">Club</span>
        <span className="w-8 text-center">Pts</span>
        <span className="w-6 text-center">GP</span>
        <span className="w-8 text-right">GD</span>
      </div>
      {standings.map((row, i) => {
        const label = row.team.common_name ?? row.team.team_name ?? ""
        const isTop3 = i < 3
        return (
          <div
            key={row.team.team_id}
            className="group flex flex-wrap items-center gap-y-1 gap-x-2 py-[7px] border-b border-border/30 last:border-0 hover:bg-muted/25 transition-colors rounded-sm -mx-1.5 px-1.5"
          >
            <span
              className={cn(
                "w-5 shrink-0 text-right tabular-nums font-mono",
                i === 0
                  ? "text-xs font-bold text-amber-400"
                  : i === 1
                  ? "text-xs font-semibold text-zinc-400"
                  : i === 2
                  ? "text-xs font-semibold text-amber-700/90 dark:text-amber-600"
                  : "text-xs text-muted-foreground/30"
              )}
            >
              {i + 1}
            </span>
            {/* min-w so the club name+logo claims most of the row's width on
                mobile — if the stat columns don't fit alongside it, they
                wrap to their own line below instead of clipping the logo. */}
            <div className="flex flex-1 min-w-[55%] sm:min-w-0 items-center gap-2">
              <TeamLogoImg team={row.team} />
              <span
                className={cn(
                  "text-xs truncate",
                  isTop3 ? "font-semibold" : "font-medium"
                )}
              >
                {label}
              </span>
            </div>
            <span className="w-8 ml-2 sm:ml-0 text-center text-xs font-bold tabular-nums">
              {row.points ?? "—"}
            </span>
            <span className="w-6 text-center text-xs tabular-nums text-muted-foreground/55">
              {row.gp ?? "—"}
            </span>
            <span className="w-8 text-right text-xs tabular-nums">
              <GdLabel gd={row.gd} />
            </span>
          </div>
        )
      })}
    </div>
  )
}

function GapView({ standings }: { standings: TeamRank[] }) {
  if (standings.length === 0) return null

  const maxPts = Math.max(...standings.map((r) => r.points ?? 0))
  const minPts = Math.min(...standings.map((r) => r.points ?? 0))

  const byPoints = new Map<number, TeamRank[]>()
  for (const row of standings) {
    const pts = row.points ?? 0
    const existing = byPoints.get(pts)
    if (existing) existing.push(row)
    else byPoints.set(pts, [row])
  }

  const rows: { pts: number; teams: TeamRank[] }[] = []
  for (let pts = maxPts; pts >= minPts; pts--) {
    rows.push({ pts, teams: byPoints.get(pts) ?? [] })
  }

  return (
    <div>
      {rows.map(({ pts, teams }) => {
        const hasTeams = teams.length > 0
        return (
          <div key={pts} className="flex items-center gap-3 h-[30px]">
            <span
              className={cn(
                "w-8 shrink-0 text-right tabular-nums font-mono select-none",
                hasTeams
                  ? "text-sm font-bold text-foreground"
                  : "text-xs text-muted-foreground/20"
              )}
            >
              {pts}
            </span>
            <div className="flex items-center gap-1">
              {teams.map((row) => (
                <TeamLogoImg
                  key={row.team.team_id}
                  team={row.team}
                  className="size-[22px]"
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function TableSkeleton() {
  return (
    <div>
      <div className="flex items-center gap-2 pb-1.5 border-b border-border/60">
        <Skeleton className="h-2 w-5 shrink-0" />
        <Skeleton className="h-2 flex-1" />
        <Skeleton className="h-2 w-8" />
        <Skeleton className="h-2 w-6" />
        <Skeleton className="h-2 w-8" />
      </div>
      {Array.from({ length: 18 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2 py-[7px] border-b border-border/30 last:border-0"
        >
          <Skeleton className="h-2.5 w-5 shrink-0" />
          <div className="flex-1 flex items-center gap-2">
            <Skeleton className="size-5 rounded shrink-0" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-3 w-8" />
          <Skeleton className="h-2.5 w-6" />
          <Skeleton className="h-2.5 w-8" />
        </div>
      ))}
    </div>
  )
}

function FullCardSkeleton() {
  return (
    <div className="w-full space-y-3 border border-border rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-9 w-[90px]" />
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-7 w-32" />
      </div>
      <TableSkeleton />
    </div>
  )
}

// "Top 5 Leagues" pools every team from TOP_LEAGUE_IDS into one combined
// ranking (sorted by points, then goal difference) rather than printing 5
// separate per-league tables. A separate component (not inlined in
// LeagueRanks) so its 5 fixed hook calls only run while this mode is
// actually mounted.
function TopFiveLeaguesView({ season, view }: { season: number; view: ViewMode }) {
  // Fixed-length array (TOP_LEAGUE_IDS never changes), so calling the hook
  // once per id in a loop here is safe — same hooks, same order, every render.
  const queries = TOP_LEAGUE_IDS.map((id) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useFetchLeagueStandings({ league_id: id, season_year: season })
  )

  const isLoading = queries.some((q) => q.isLoading)
  const combined = queries
    .flatMap((q) => q.data?.data ?? [])
    .sort((a, b) => (b.points ?? 0) - (a.points ?? 0) || (b.gd ?? 0) - (a.gd ?? 0))

  if (isLoading) return <TableSkeleton />
  if (combined.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No standings available.</p>
  }

  return view === "table" ? <TableView standings={combined} /> : <GapView standings={combined} />
}

export function LeagueRanks() {
  const [leagueId, setLeagueId] = useState(DEFAULT_LEAGUE_ID)
  const [season, setSeason] = useState(DEFAULT_SEASON)
  const [view, setView] = useState<ViewMode>("table")

  // Leagues rarely refetch (long staleTime), so this only stays true for the
  // component's very first mount — used to hold the whole card behind one
  // skeleton instead of letting the header/controls commit first and the
  // table pop in a beat later.
  const { data: leaguesData, isLoading: leaguesLoading } = useFetchLeagues()

  const isTop5 = leagueId === TOP5_SENTINEL

  const { data, isLoading } = useFetchLeagueStandings({
    // Disabled (via league_id <= 0) while Top 5 mode is active — that mode
    // fetches its own 5 standings independently in TopFiveLeaguesView.
    league_id: isTop5 ? 0 : leagueId,
    season_year: season,
  })

  const standings = data?.data ?? []

  if (leaguesLoading && !leaguesData) {
    return <FullCardSkeleton />
  }

  return (
    <div className="w-full space-y-3 border border-border rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold tracking-tight">League Table</h2>
        <Select value={String(season)} onValueChange={(v) => setSeason(Number(v))}>
          <SelectTrigger className="w-[90px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[260px] overflow-y-auto">
            {YEAR_OPTIONS.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <LeagueFlagPicker value={leagueId} onChange={setLeagueId} />

      <div className="flex justify-end">
        <div className="flex items-center gap-0.5 rounded-md bg-muted/50 p-0.5">
          <button
            onClick={() => setView("table")}
            className={cn(
              "px-2.5 py-1 text-xs font-medium rounded-[4px] transition-colors",
              view === "table"
                ? "bg-red-600 text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Table
          </button>
          <button
            onClick={() => setView("gap")}
            className={cn(
              "px-2.5 py-1 text-xs font-medium rounded-[4px] transition-colors",
              view === "gap"
                ? "bg-red-600 text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Gap
          </button>
        </div>
      </div>

      <div>
        {isTop5 ? (
          <TopFiveLeaguesView season={season} view={view} />
        ) : isLoading ? (
          <TableSkeleton />
        ) : standings.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No standings available.
          </p>
        ) : view === "table" ? (
          <TableView standings={standings} />
        ) : (
          <GapView standings={standings} />
        )}
      </div>
    </div>
  )
}
